import { HorizontalScroll } from "../../components/HorizontalScroll";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Download } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, ActionButton, EmptyState, SelectInput } from "../../components/common";
import { useRentalDispatchData } from "../../hooks/useRentalDispatchData";
import { useMapsLink } from "../../hooks/useMapsLink";
import { useDispatchBackup } from "../../hooks/useDispatchBackup";
import { RentalDispatchCard } from "../../components/fractank/RentalDispatchCard";
import { RentalDeliveryDialog, RentalReturnDialog, RentalDispatchDialog } from "../../components/fractank/RentalDispatchDialogs";
import { CreateProformaDialog } from "../../components/fractank/CreateProformaDialog";
import { COLUMNS, BOARD_VIEWS, EMPTY_RENTAL, newDeliveryItem, stageLabel, deliveryItemsFor } from "../../lib/rentalDispatch";

export default function FracDispatch() {
  const { user } = useAuth();
  const canDispatch = ["admin", "shop_manager", "dispatcher"].includes(user?.role);
  const canCreate = ["admin", "shop_manager", "dispatcher", "salesperson"].includes(user?.role);
  const canBill = ["admin", "shop_manager", "dispatcher", "salesperson"].includes(user?.role);
  const [proformaFor, setProformaFor] = useState(null);
  const [proformas, setProformas] = useState([]);
  const { dispatches, customers, assets, items, load } = useRentalDispatchData();
  const loadProformas = useCallback(() => {
    if (!canBill) return;
    api.get("/proformas").then((r) => setProformas(r.data)).catch(() => {});
  }, [canBill]);
  useEffect(() => { loadProformas(); }, [loadProformas]);
  const proformasByDispatch = useMemo(() => {
    const m = {};
    proformas.forEach((p) => (p.source_dispatch_ids || [p.source_dispatch_id]).forEach((id) => { if (id) (m[id] = m[id] || []).push(p); }));
    return m;
  }, [proformas]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_RENTAL);
  const [deliverFor, setDeliverFor] = useState(null);
  const [mapsLink, setMapsLink] = useState("");
  const [deliverItems, setDeliverItems] = useState([newDeliveryItem()]);
  const [deliverToYard, setDeliverToYard] = useState(false);
  const [returnFor, setReturnFor] = useState(null);
  const [returnDriver, setReturnDriver] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [returnBillingEnd, setReturnBillingEnd] = useState("");
  const [busy, setBusy] = useState(false);
  const { exporting, exportError, backupTickets } = useDispatchBackup();
  const [boardView, setBoardView] = useState("standard");
  const [customerFilter, setCustomerFilter] = useState("");
  const [expandedTickets, setExpandedTickets] = useState(new Set());
  const compressed = boardView === "compressed";
  const toggleTicket = (id) => setExpandedTickets((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  useMapsLink(mapsLink, setForm);
  const customerOptions = useMemo(() => [...customers].sort((a, b) => a.name.localeCompare(b.name)).map((c) => ({ value: c.id, label: c.name })), [customers]);
  const customerFilterOptions = useMemo(() => [{ value: "", label: "All Customers" }, ...customerOptions], [customerOptions]);
  const itemOptions = useMemo(() => {
    const cust = deliverFor?.customer_id;
    const relevant = items.filter((it) => it.customer_id === cust);
    const uomLabel = (it) => (it.unit_of_measure || "day").toLowerCase().replace(/s$/, "");
    return [{ value: "", label: "No item — manual / default rate" }, ...[...relevant].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((it) => ({ value: it.id, label: `${it.name}${it.rate != null ? ` — $${it.rate}/${uomLabel(it)}` : ""}` }))];
  }, [items, deliverFor]);
  const openDelivery = (dispatch) => { setDeliverFor(dispatch); setDeliverItems(deliveryItemsFor(dispatch)); setDeliverToYard(!!dispatch.return_to_yard); };

  const deleteDispatch = async (d) => {
    const warning = d.status === "on_rent" ? " Its Sales Order will be retained and marked VOID." : "";
    if (!window.confirm(`Delete ${d.dispatch_number}?${warning} This cannot be undone.`)) return;
    try {
      await api.delete(`/fractank/dispatches/${d.id}`);
      toast.success(`${d.dispatch_number} deleted`);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const deployedIds = useMemo(
    () => new Set(dispatches.filter((d) => ["delivered", "on_rent"].includes(d.status)).flatMap((d) => (d.asset_ids || []).filter((id) => d.job_type === "return_pickup" && d.return_to_yard ? !(d.yard_arrived_asset_ids || []).includes(id) : !(d.picked_up_asset_ids || []).includes(id)))),
    [dispatches]
  );
  const availableAssets = useMemo(
    () => {
      const mixing = deliverFor?.rental_type === "mixing_plant";
      return assets
        .filter((a) => {
          const op = (a.operation_status || "").toLowerCase();
          const inMaintenance = op.startsWith("oos") || op === "in_shop";
          if (inMaintenance || deployedIds.has(a.id)) return false;
          if (mixing) return !(a.department || "").toLowerCase().includes("vacuum");
          return (a.fixed_asset_id || "").toUpperCase().startsWith("RENTAL");
        })
        .sort((a, b) => (a.asset_name || "").localeCompare(b.asset_name || ""));
    },
    [assets, deployedIds, deliverFor]
  );

  const assignmentAssets = useMemo(() => {
    if (!deliverFor) return availableAssets;
    if (deliverFor.job_type === "return_pickup") {
      const source = dispatches.find((d) => d.id === deliverFor.return_for_dispatch_id);
      const remaining = (source?.asset_ids || deliverFor.asset_ids || []).filter((id) => !(source?.picked_up_asset_ids || []).includes(id));
      return assets.filter((asset) => remaining.includes(asset.id));
    }
    return assets.filter((asset) => (deliverFor.asset_ids || []).includes(asset.id) || availableAssets.some((available) => available.id === asset.id));
  }, [deliverFor, availableAssets, assets, dispatches]);

  const driverOptions = useMemo(
    () => [...new Set(assets.map((a) => (a.driver_name || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [assets]
  );
  const selectedAssets = useMemo(() => deliverItems.flatMap((it) => it.assets).filter(Boolean), [deliverItems]);
  const selectedDrivers = useMemo(() => deliverItems.map((it) => it.driver).filter(Boolean), [deliverItems]);

  const boardDispatches = useMemo(
    () => dispatches.filter((d) => COLUMNS.includes(d.status) && (!customerFilter || d.customer_id === customerFilter)),
    [dispatches, customerFilter]
  );
  const byStatus = useMemo(() => {
    const g = {};
    COLUMNS.forEach((c) => { g[c] = []; });
    boardDispatches.forEach((d) => { g[d.status].push(d); });
    return g;
  }, [boardDispatches]);

  const openEdit = (d) => {
    setEditing(d);
    setForm({
      customer_id: d.customer_id || "",
      delivery_location: d.delivery_location || "",
      lat: d.lat != null ? String(d.lat) : "",
      lng: d.lng != null ? String(d.lng) : "",
      company_man: d.company_man || "",
      requester_id: d.requester_id,
      order_taken_date: (d.order_taken_date || "").slice(0, 10),
      deliver_by_date: (d.deliver_by_date || "").slice(0, 10),
      billing_start_date: (d.billing_start_date || "").slice(0, 10),
      notes: d.notes || "",
      rental_type: d.rental_type || "frac_tank",
      return_to_yard: !!d.return_to_yard,
      location_id: d.location_id || "",
      location_name: d.location_name || "",
      initial_stage: d.initial_stage || d.current_stage || "A",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { customer_id: form.customer_id, delivery_location: form.delivery_location, return_to_yard: form.return_to_yard };
      if (!editing) payload.rental_type = form.rental_type;
      if (form.lat !== "" && form.lat != null) payload.lat = parseFloat(form.lat);
      if (form.lng !== "" && form.lng != null) payload.lng = parseFloat(form.lng);
      if (form.requester_id !== undefined) payload.requester_id = form.requester_id || null;
      if (form.order_taken_date) payload.order_taken_date = form.order_taken_date;
      if (form.deliver_by_date) payload.deliver_by_date = form.deliver_by_date;
      if (form.billing_start_date) payload.billing_start_date = form.billing_start_date;
      if (form.notes) payload.notes = form.notes;
      if (form.location_id) payload.location_id = form.location_id;
      payload.initial_stage = form.initial_stage || "A";
      if (editing) {
        await api.patch(`/fractank/dispatches/${editing.id}`, payload);
        toast.success(`${editing.dispatch_number} updated`);
      } else {
        await api.post("/fractank/dispatches", payload);
        toast.success("Rental dispatch scheduled");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY_RENTAL);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (d, status) => {
    try {
      await api.patch(`/fractank/dispatches/${d.id}`, { status });
      toast.success(`${d.dispatch_number} → ${stageLabel(status)}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const setItem = (idx, patch) => setDeliverItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const setItemAsset = (i, ai, val) => setItem(i, { assets: deliverItems[i].assets.map((x, j) => (j === ai ? val : x)) });

  const submitDelivery = async (e) => {
    e.preventDefault();
    const cleaned = deliverItems.map((it) => ({
      asset_ids: it.service_only ? [] : [...new Set(it.assets.filter(Boolean))],
      driver: it.driver,
      rental_item_id: it.rental_item_id || null,
      service_only: !!it.service_only,
      ...(it.notes ? { notes: it.notes } : {}),
    }));
    if (cleaned.some((it) => !it.service_only && it.asset_ids.length === 0)) {
      toast.error("Each rental item needs equipment (or check Service Item)");
      return;
    }
    if (cleaned.some((it) => !it.service_only && !it.driver)) {
      toast.error("Every equipment item needs a driver");
      return;
    }
    if (cleaned.some((it) => !it.rental_item_id)) {
      toast.error("Every item needs a Product / Service Item to charge");
      return;
    }
    const allAssets = [...new Set(cleaned.flatMap((it) => it.asset_ids))];
    const allDrivers = [...new Set(cleaned.map((it) => it.driver).filter(Boolean))];
    setBusy(true);
    try {
      await api.patch(`/fractank/dispatches/${deliverFor.id}`, {
        status: "delivered", asset_ids: allAssets, drivers: allDrivers, driver_name: allDrivers[0], items: cleaned, return_to_yard: deliverToYard,
      });
      toast.success(`${deliverFor.dispatch_number} → IN-TRANSIT (${cleaned.length} item${cleaned.length > 1 ? "s" : ""}, ${allAssets.length} asset${allAssets.length > 1 ? "s" : ""}, ${allDrivers.length} driver${allDrivers.length > 1 ? "s" : ""})`);
      setDeliverFor(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const openReturn = (d) => {
    setReturnFor(d);
    setReturnDriver(d.driver_name || "");
    setReturnDate("");
  };

  const submitReturn = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/fractank/dispatches", {
        customer_id: returnFor.customer_id,
        delivery_location: returnFor.delivery_location,
        job_type: "return_pickup",
        return_for_dispatch_id: returnFor.id,
        asset_ids: returnFor.asset_ids || [],
        return_driver: returnDriver,
        pickup_date: returnDate,
        notes: `Return pickup for ${returnFor.dispatch_number}`,
      });
      toast.success(`Return Pickup Job ${r.data.dispatch_number} created — NEW`);
      setReturnFor(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const onDropCard = (e, col) => {
    e.preventDefault();
    e.currentTarget.classList.remove("border-amber-500/60", "bg-amber-500/5");
    const id = e.dataTransfer.getData("text/plain");
    const d = dispatches.find((x) => x.id === id);
    if (d && d.status !== col) setStatus(d, col);
  };

  const onDragOverCol = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add("border-amber-500/60", "bg-amber-500/5");
  };

  const onDragLeaveCol = (e) => {
    e.currentTarget.classList.remove("border-amber-500/60", "bg-amber-500/5");
  };

  return (
    <div data-testid="frac-dispatch-page">
      <PageHeader title="Rental Dispatch Board" subtitle="Frac tanks, gas busters, hoses & pumps out to location" testId="frac-dispatch-header">
        <div data-testid="frac-board-legend" className="flex flex-col gap-1 px-3 py-2 rounded-md border border-slate-800 bg-slate-900/40">
          <span className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-pink-800"><span className="w-2.5 h-2.5 rounded-full bg-pink-400 border border-pink-500 flex-shrink-0" />Mixing Plant Rental (MPR)</span>
          <span className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-slate-400"><span className="w-2.5 h-2.5 rounded-full bg-sky-400 border border-sky-500 flex-shrink-0" />Blue = Driver accepted</span>
          <span className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-slate-400"><span className="w-2.5 h-2.5 rounded-full bg-yellow-300 border border-yellow-400 flex-shrink-0" />Yellow = All drivers accepted</span>
          <span className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-slate-400"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400 border border-emerald-500 flex-shrink-0" />Green = Return Pickup Job</span>
        </div>
        {canCreate && (
          <ActionButton testId="frac-create-dispatch-button" onClick={() => setOpen(true)}>
            <Plus size={16} /> New Rental Dispatch
          </ActionButton>
        )}
      </PageHeader>
      <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="frac-board-view-toolbar">
        <div role="group" aria-label="Board view" data-testid="frac-board-view-toggle" className="inline-flex gap-1 rounded-lg border border-slate-700 bg-card p-1">
          {BOARD_VIEWS.map((view) => (
            <ActionButton key={view} type="button" testId={`frac-dispatch-view-${view}-btn`} variant={boardView === view ? "primary" : "secondary"} aria-pressed={boardView === view} onClick={() => { setBoardView(view); setExpandedTickets(new Set()); }} className="min-h-10 !px-3 !py-2 text-xs">
              {view === "standard" ? "Standard" : "Compressed"}
            </ActionButton>
          ))}
        </div>
        <div className="w-full sm:w-64 min-w-0">
          <SelectInput testId="frac-dispatch-customer-filter" aria-label="Filter board by customer" value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)} className="min-h-11" options={customerFilterOptions} />
        </div>
        {customerFilter && <ActionButton type="button" testId="frac-dispatch-clear-customer-filter" variant="secondary" onClick={() => setCustomerFilter("")} className="min-h-11">Clear Filter</ActionButton>}
        {canDispatch && <ActionButton type="button" testId="frac-dispatch-backup-csv-button" variant="secondary" onClick={backupTickets} disabled={exporting} aria-busy={exporting} title="Download all saved Rental Dispatch tickets, including closed tickets, regardless of customer filter" className="min-h-11"><Download size={16} /> {exporting ? "Preparing Backup…" : "Backup All Tickets (.CSV)"}</ActionButton>}
        {compressed && <p data-testid="frac-board-compressed-help" className="text-xs text-slate-500">Click a ticket to expand its details.</p>}
        {canDispatch && <p data-testid="frac-dispatch-backup-help" className="w-full text-xs text-slate-500">Backup includes all saved tickets in every stage, including closed. Customer filters do not limit the download.</p>}
        {exportError && <p data-testid="frac-dispatch-backup-error" role="alert" className="w-full text-sm text-red-600">{exportError}</p>}
      </div>

      {boardDispatches.length === 0 ? (
        <EmptyState message={customerFilter ? "No open rental dispatches for this customer" : "No rental dispatches scheduled"} testId="frac-dispatch-empty" />
      ) : (
        <HorizontalScroll contentClassName="flex gap-4 pb-4" testId="frac-dispatch-board-row">
          {COLUMNS.map((col) => (
            <div
              key={col}
              className={`rounded-lg p-3 min-h-[200px] w-72 flex-shrink-0 transition-colors duration-150 border bg-slate-900/40 border-slate-800`}
              data-testid={`frac-dispatch-column-${col}`}
              onDragOver={onDragOverCol}
              onDragLeave={onDragLeaveCol}
              onDrop={(e) => onDropCard(e, col)}
            >
              <div className="flex items-center justify-between px-1 mb-3">
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400">{stageLabel(col)}</span>
                <span className="text-xs font-mono text-amber-500">{byStatus[col].length}</span>
              </div>
              <div className="space-y-3">
                {byStatus[col].map((d) => (
                    <RentalDispatchCard key={d.id} dispatch={d} compressed={compressed} expanded={expandedTickets.has(d.id)} canDispatch={canDispatch} canBill={canBill} proformas={proformasByDispatch[d.id] || []} onToggle={toggleTicket} onDelete={deleteDispatch} onEdit={openEdit} onAssign={openDelivery} onReturn={openReturn} onStatus={setStatus} onStageSaved={load} onProforma={setProformaFor} />
                  ))}
              </div>
            </div>
          ))}
        </HorizontalScroll>
      )}

      <RentalDeliveryDialog {...{ deliverFor, setDeliverFor, submitDelivery, deliverItems, setDeliverItems, deliverToYard, setDeliverToYard, selectedAssets, driverOptions, selectedDrivers, setItem, setItemAsset, busy, itemOptions }} availableAssets={assignmentAssets} />
      <RentalReturnDialog {...{ returnFor, setReturnFor, submitReturn, returnDriver, setReturnDriver, returnDate, setReturnDate, driverOptions, busy }} />
      <RentalDispatchDialog {...{ open, setOpen, editing, setEditing, form, setForm, mapsLink, setMapsLink, customerOptions, submit, busy }} />
      {proformaFor && <CreateProformaDialog dispatch={proformaFor} onClose={() => setProformaFor(null)} onCreated={() => { load(); loadProformas(); }} />}
    </div>
  );
}
